import { useState } from 'react';

export type FieldValues = Record<string, any>;

const useForm = <T extends FieldValues>({
  defaultValues,
  handleValidation,
}: {
  defaultValues: T;
  handleValidation: (fieldName: keyof T, value: any, values: T) => string;
}) => {
  const [values, setValues] = useState<T>(defaultValues);
  const [errors, setErrors] = useState({} as Partial<Record<keyof T, string>>);

  const setValue = (fieldName: string, value: any) => {
    setErrors({
      ...errors,
      [fieldName]: '',
    });

    setValues({
      ...values,
      [fieldName]: value,
    });
  };

  const setSomeErrors = (newErrors: Partial<Record<keyof T, string>>) => {
    setErrors({
      ...errors,
      ...newErrors,
    });
  };

  const setSomeValues = (manyValues: { [key: string]: any }) => {
    setErrors({
      ...errors,
      ...Object.keys(manyValues).reduce<Record<string, string>>((acc, key) => {
        acc[key] = '';

        return acc;
      }, {}),
    });

    setValues({
      ...values,
      ...manyValues,
    });
  };

  const resetForm = (resetValues?: T) => {
    setValues(resetValues || defaultValues);
    setErrors({} as Partial<Record<keyof T, string>>);
  };

  const validate = (fieldName: keyof T, value: any) => {
    const errorMessage = handleValidation(fieldName, value, values);

    setErrors({
      ...errors,
      [fieldName]: errorMessage,
    });

    return !errorMessage;
  };

  const validateAll = () => {
    let allValid = true;
    const newErrors = {} as Partial<Record<keyof T, string>>;

    for (const fieldName of Object.keys(values) as (keyof T)[]) {
      const value = values[fieldName];

      const errorMessage = handleValidation(fieldName, value, values);

      if (errorMessage) {
        allValid = false;
        newErrors[fieldName] = errorMessage;
      }
    }

    setErrors(newErrors);

    return allValid;
  };

  const handleSubmit = (callback: () => Promise<void>) => async (event: React.SyntheticEvent<HTMLElement>) => {
    event.preventDefault();

    if (validateAll()) {
      await callback();
    }
  };

  return {
    values: values,
    errors: errors,
    setValue: setValue,
    validateField: validate,
    handleSubmit: handleSubmit,
    resetForm: resetForm,
    setValues: setSomeValues,
    setErrors: setSomeErrors,
  };
};

export default useForm;
